/**
 * Service Worker registration for background session keep-alive
 */
import { logDTEvent, isDynatraceTrackingEnabled } from "../analytics/dynatrace";
import { DT_EVENTS, DT_ATTRIBUTES } from "../constants/dynatrace";
import { isExpiryFeatureEnabled } from "./tokenExpiryHelpers";

const KEEP_ALIVE_SERVICE_WORKER_PATH = "/keepalive-service-worker.js";
const KEEP_ALIVE_TAG = "session-keep-alive";

const KEEP_ALIVE_MESSAGE_TYPES = {
  SET_CONFIG: "SET_KEEPALIVE_CONFIG",
  UPDATE_TOKEN: "UPDATE_REFRESH_TOKEN",
  CLEAR_CONFIG: "CLEAR_KEEPALIVE_CONFIG",
  LOG_DYNATRACE_EVENT: "LOG_DYNATRACE_EVENT",
};

let cbFuncSucc;
let cbFuncFail;

/**
 * Register the keep-alive service worker
 */
export async function registerKeepAliveServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.warn("[KeepAlive] Service Workers not supported");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(KEEP_ALIVE_SERVICE_WORKER_PATH, {
      scope: "/",
    });

    console.log("[KeepAlive] Service Worker registered successfully");

    // Periodic sync code was here but removed since it's highly unreliable

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);

    return registration;
  } catch (error) {
    console.error("[KeepAlive] Failed to register Service Worker:" + error);
    return null;
  }
}

/**
 * Request immediate keep-alive from service worker
 */
export async function requestImmediateKeepAlive(useAVSRefreshToken = false) {
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
    console.warn("[KeepAlive] Service Worker not available for immediate sync");
    return;
  }

  try {
    // Send message to service worker for immediate sync
    navigator.serviceWorker.controller.postMessage({
      type: "FORCE_KEEP_ALIVE",
      timestamp: new Date().toISOString(),
      useAVSRefreshToken: useAVSRefreshToken,
    });

    console.log("[KeepAlive] Immediate keep-alive requested");
  } catch (error) {
    console.error("[KeepAlive] Failed to request immediate keep-alive:", error);
  }
}

/**
 * Unregister the keep-alive service worker
 */
export async function unregisterKeepAliveServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      // Unregister periodic sync if available
      if ("periodicSync" in registration) {
        try {
          await registration.periodicSync.unregister(KEEP_ALIVE_TAG);
          console.log("[KeepAlive] Periodic sync unregistered");
        } catch (error) {
          console.warn("[KeepAlive] Failed to unregister periodic sync:", error);
        }
      }
    }

    console.log("[KeepAlive] Service Worker unregistered");
  } catch (error) {
    console.error("[KeepAlive] Failed to unregister Service Worker:", error);
  }
}

/**
 * Update service worker config (renew URL and/or refresh token)
 * @param {*} successCb
 * @param {*} failureCb
 * @param {Object} config
 * @param {string} [config.renewUrl]
 * @param {string} [config.refreshToken]
 * @param {Object} [config.videoPlaying]
 * @param {boolean} [config.isLegacy]
 */
export async function updateKeepAliveServiceWorkerConfig(
  successCb,
  failureCb,
  { renewUrl, refreshToken, videoPlaying, isLegacy } = {}
) {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const target = await getServiceWorkerTarget();
  if (!target) {
    return;
  }

  if (successCb) {
    cbFuncSucc = successCb;
  }

  if (failureCb) {
    cbFuncFail = failureCb;
  }

  target.postMessage({
    type: KEEP_ALIVE_MESSAGE_TYPES.SET_CONFIG,
    payload: {
      renewUrl,
      refreshToken,
      videoPlaying,
      isLegacy,
    },
  });
}

/**
 * Update only refresh token in service worker
 * @param {string} refreshToken
 */
export async function updateKeepAliveServiceWorkerToken(refreshToken) {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const target = await getServiceWorkerTarget();
  if (!target) {
    return;
  }

  target.postMessage({
    type: KEEP_ALIVE_MESSAGE_TYPES.UPDATE_TOKEN,
    payload: { refreshToken },
  });
}

/**
 * Clear stored keep-alive config in service worker
 */
export async function clearKeepAliveServiceWorkerConfig() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const target = await getServiceWorkerTarget();
  if (!target) {
    return;
  }

  target.postMessage({
    type: KEEP_ALIVE_MESSAGE_TYPES.CLEAR_CONFIG,
  });
}

/**
 * Handle messages from service worker
 */
function handleServiceWorkerMessage(event) {
  const { type, timestamp, data, attributes } = event.data;

  if (type === "SESSION_RENEWED") {
    console.log("[KeepAlive] Session renewed by Service Worker at", timestamp);
    // Optionally dispatch Redux action or callback
    // dispatch({ type: 'SESSION_RENEWED', payload: { timestamp } });
    if (cbFuncSucc && data) {
      cbFuncSucc(event.data);
    }
  } else if (type === "SESSION_FAIL") {
    console.log("[KeepAlive] Session failed to renew by Service Worker at", timestamp);
    if (cbFuncFail && data) {
      cbFuncFail(event.data);
    }
  } else if (type === KEEP_ALIVE_MESSAGE_TYPES.LOG_DYNATRACE_EVENT) {
    if (!isExpiryFeatureEnabled()) {
      return;
    }

    logDTEvent(
      DT_EVENTS.TOKEN_REFRESH_ERROR,
      {
        [DT_ATTRIBUTES.ERROR_CODE]: attributes?.errorCode,
        [DT_ATTRIBUTES.API_PATH]: attributes?.apiPath,
        [DT_ATTRIBUTES.APP_STATE]: attributes?.appState,
        [DT_ATTRIBUTES.LOGGED_OUT]: attributes?.loggedOut,
      },
      isDynatraceTrackingEnabled()
    );
  }
}

/**
 * Manual fallback keep-alive (if periodic sync not available)
 * Call this from KeepAliveManager to ensure keep-alive even without periodic sync
 */
export function setupManualServiceWorkerKeepAlive(intervalSeconds = 600) {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  // Setup interval to request keep-alive from service worker
  const intervalId = setInterval(() => {
    if (navigator.serviceWorker.controller) {
      requestImmediateKeepAlive();
    }
  }, intervalSeconds * 1000);

  // Return cleanup function
  return () => clearInterval(intervalId);
}

async function getServiceWorkerTarget() {
  if (navigator.serviceWorker.controller) {
    return navigator.serviceWorker.controller;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return registration.active || navigator.serviceWorker.controller || null;
  } catch (error) {
    console.warn("[KeepAlive] Service Worker not ready:", error);
    return null;
  }
}
